import { useNavigate } from "react-router-dom"
import { ArrowRightOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { ApiTypes } from "types"
import NotificationEventTypeId from "types/enums/NotificationEventTypeId"
import { ReactComponent as IconMonitoring } from "assets/svg/menu-monitoring.svg"
import { ReactComponent as IconPerformanceTest } from "assets/svg/menu-performance.svg"
import { ReactComponent as CheckCircleIcon } from "assets/svg/notifications/icon-check-circle.svg"
import { ReactComponent as EnvelopeIcon } from "assets/svg/notifications/icon-envelope.svg"
import { ReactComponent as ExclamationTriangleIcon } from "assets/svg/notifications/icon-exclamation-triangle.svg"

dayjs.extend(utc)

import { OldSubscriptionPlanTitles } from "constants/SubscriptionPlanTitles"

import {
  AvatarWrapper,
  CardContainer,
  ContentWrapper,
  CustomerName,
  DeviceLocationLine,
  IconContainer,
  IssueTest,
  NotificationContainer,
  NotificationContent,
  ReadIndicator,
  ReportLink,
  StyledTooltip,
  Subtext,
  SubtextLine,
  TimeStamp,
  Title,
} from "./styled"

interface NotificationCardProps {
  notification: ApiTypes.Model.Notification
  onToggleRead: (id: number, isRead: boolean) => void
  onCardClick?: () => void
}

export const NotificationCard = ({ notification, onToggleRead, onCardClick }: NotificationCardProps) => {
  const navigate = useNavigate()

  const getAvatarType = (): "email" | "positive" | "warning" | "report" | "perftest" => {
    if (notification.event_type_id === NotificationEventTypeId.MonthlyEngagementReport) {
      return "report"
    }

    if (notification.event_type_id === NotificationEventTypeId.PerformanceTestStopped) {
      return "perftest"
    }

    if (
      [
        NotificationEventTypeId.AlertTriggered,
        NotificationEventTypeId.SystemEfficiencyAlert,
        NotificationEventTypeId.PerformanceTestIssue,
      ].includes(notification.event_type_id)
    ) {
      return "warning"
    }

    if (
      [NotificationEventTypeId.NewThermostatInstalled, NotificationEventTypeId.SubscriptionPlanChanged].includes(
        notification.event_type_id,
      )
    ) {
      return "positive"
    }

    return "email"
  }

  const getAvatarIcon = (type: "email" | "positive" | "warning" | "report" | "perftest") => {
    switch (type) {
      case "positive":
        return <CheckCircleIcon />
      case "report":
        return <IconMonitoring />
      case "warning":
        return <ExclamationTriangleIcon />
      case "perftest":
        return <IconPerformanceTest />
      default:
        return <EnvelopeIcon />
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const utcDate = dayjs.utc(dateString)
    const localDate = utcDate.local()
    const now = dayjs()
    const diffInSeconds = Math.floor(now.diff(localDate, "second"))

    if (diffInSeconds < 60) return "Just now"
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`

    return localDate.format("MMM D, YYYY")
  }

  const formatReportingPeriod = (from?: string, to?: string) => {
    const startDate = from ? dayjs(from) : null
    const endDate = to ? dayjs(to) : null

    if (!startDate?.isValid() || !endDate?.isValid()) {
      return from || to || null
    }

    if (startDate.isSame(endDate, "month")) {
      return `${startDate.format("MMM D")}\u2013${endDate.format("D, YYYY")}`
    }

    if (startDate.isSame(endDate, "year")) {
      return `${startDate.format("MMM D")}\u2013${endDate.format("MMM D, YYYY")}`
    }

    return `${startDate.format("MMM D, YYYY")}\u2013${endDate.format("MMM D, YYYY")}`
  }

  const handleToggleRead = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onToggleRead(notification.id, notification.is_read)
  }

  const getTitle = () => {
    if (notification.event_type_id === NotificationEventTypeId.SystemEfficiencyAlert) {
      return <div>An alert has been triggered on a device. Check the Monitoring page for details.</div>
    }
    if (notification.event_type_id === NotificationEventTypeId.PerformanceTestIssue) {
      return (
        <div>
          Performance Test Result: <IssueTest>Issue</IssueTest>
          <div>View the test result details on Monitoring page</div>
        </div>
      )
    }

    return notification.subject
  }

  const renderAlertContent = () => {
    const eventParams = notification.event_params

    switch (notification.event_type_id) {
      case NotificationEventTypeId.AlertTriggered:
      case NotificationEventTypeId.SystemEfficiencyAlert:
        return (
          <>
            <SubtextLine>Alert Type: {eventParams.alert_type}</SubtextLine>
            <SubtextLine>Customer Email: {eventParams.customer_email}</SubtextLine>
            <SubtextLine>Serial Number: {eventParams.device_sn}</SubtextLine>
          </>
        )
      case NotificationEventTypeId.SubscriptionPlanChanged:
        return (
          <>
            <SubtextLine>
              New Plan: Nuve {eventParams.subscription_plan && OldSubscriptionPlanTitles[eventParams.subscription_plan]}
            </SubtextLine>
          </>
        )

      case NotificationEventTypeId.MonthlyEngagementReport: {
        const reportingPeriod = formatReportingPeriod(
          eventParams.reporting_period_from,
          eventParams.reporting_period_to,
        )

        return (
          <>
            {reportingPeriod && <SubtextLine>{reportingPeriod}</SubtextLine>}
            <ReportLink>
              View report <ArrowRightOutlined />
            </ReportLink>
          </>
        )
      }

      case NotificationEventTypeId.PerformanceTestStopped: {
        const deviceLocation =
          eventParams.device_name && eventParams.device_name === eventParams.location_name
            ? eventParams.device_name
            : [eventParams.location_name, eventParams.device_name].filter(Boolean).join(" • ")

        return (
          <>
            <SubtextLine>
              Customer <CustomerName>{eventParams.customer_email}</CustomerName> stopped the Performance Test. It has
              been rescheduled for {eventParams.rescheduled_at}.
            </SubtextLine>
            {deviceLocation && <DeviceLocationLine>{deviceLocation}</DeviceLocationLine>}
          </>
        )
      }

      default:
        return (
          <>
            <SubtextLine>Customer Email: {eventParams.customer_email}</SubtextLine>
            <SubtextLine>Serial Number: {eventParams.device_sn}</SubtextLine>
          </>
        )
    }
  }

  const handleNotificationCardClick = () => {
    if (notification.event_type_id === NotificationEventTypeId.SubscriptionPlanChanged) {
      return
    }

    if (!notification.is_read) {
      onToggleRead(notification.id, notification.is_read)
    }

    if (notification.event_type_id === NotificationEventTypeId.AlertTriggered) {
      navigate(
        `/alerts?sn=${notification.event_params.device_sn}&type=${notification.event_params.alert_type?.toLowerCase().replaceAll(" ", "_")}`,
      )
    }

    if (notification.event_type_id === NotificationEventTypeId.SystemEfficiencyAlert) {
      navigate(
        `/monitoring?sn=${notification.event_params.device_sn}&type=efficiency_issue&custom_param=${notification.event_params.base64_custom_param}`,
      )
    }

    if (notification.event_type_id === NotificationEventTypeId.NewThermostatInstalled) {
      navigate(
        `/customers/${notification.event_params.customer_id}/overview/?selectedDevice=${notification.event_params.device_sn}`,
      )
    }

    if (notification.event_type_id === NotificationEventTypeId.PerformanceTestIssue) {
      navigate(
        `/monitoring?sn=${notification.event_params.device_sn}&testId=${notification.event_params.pertfest_result_id}`,
      )
    }

    if (notification.event_type_id === NotificationEventTypeId.ThermostatOffline) {
      navigate(`/monitoring?sn=${notification.event_params.device_sn}&openOffline=true`)
    }

    if (notification.event_type_id === NotificationEventTypeId.PerformanceTestStopped) {
      navigate(`/perf-test?sn=${notification.event_params.device_sn}`)
    }

    if (notification.event_type_id === NotificationEventTypeId.MonthlyEngagementReport) {
      const reportUrl = new URL("/engagement-report", window.location.origin)

      if (notification.event_params.reporting_period_from) {
        reportUrl.searchParams.set("reporting_period_from", notification.event_params.reporting_period_from)
      }

      if (notification.event_params.reporting_period_to) {
        reportUrl.searchParams.set("reporting_period_to", notification.event_params.reporting_period_to)
      }

      window.open(reportUrl.toString(), "_blank", "noopener,noreferrer")
    }

    onCardClick?.()
  }

  const avatarType = getAvatarType()
  const tooltipId = `notification-tooltip-${notification.id}`
  const tooltipText = notification.is_read ? "Mark as unread" : "Mark as read"

  return (
    <CardContainer
      isRead={notification.is_read}
      isClickable={notification.event_type_id !== NotificationEventTypeId.SubscriptionPlanChanged}
      onClick={handleNotificationCardClick}
    >
      <NotificationContainer>
        <AvatarWrapper type={avatarType}>{getAvatarIcon(avatarType)}</AvatarWrapper>
        <ContentWrapper>
          <NotificationContent>
            <Title>{getTitle()}</Title>
            <Subtext>{renderAlertContent()}</Subtext>
          </NotificationContent>
          <TimeStamp>{formatTimeAgo(notification.created_at)}</TimeStamp>
        </ContentWrapper>
      </NotificationContainer>
      <IconContainer isRead={notification.is_read} onClickCapture={handleToggleRead} data-tooltip-id={tooltipId}>
        <ReadIndicator isRead={notification.is_read} />
      </IconContainer>
      <StyledTooltip id={tooltipId} variant="dark" orientation="bottom-end" delay={500}>
        {tooltipText}
      </StyledTooltip>
    </CardContainer>
  )
}
